import { useState } from 'react'
import {
    useNavigate,
    useNavigationType,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/addLoan'
import { PulseCard, SectionError } from 'components/states'
import {
    getLoanDemand,
    postLoanDemandComment,
    toggleLoanDemandLike,
} from 'services/loanDemands'
import { faPlain, longJalali, toman } from 'utilities/helper/format'
import { demandPriority } from 'utilities/helper/demandPriority'

/**
 * The timeline reads in the second person for the member whose request it is
 * and in the third for a manager reading somebody else's. Same rows, same
 * order — only the voice changes. Getting that wrong is how a manager ends up
 * reading «درخواستت پذیرفته شد» about a loan he granted to someone else.
 */
const EVENT_LABELS: Record<ILoanDemandEventKind, string> = {
    crt: 'درخواست ثبت شد',
    ntf: 'به مدیران صندوق اطلاع داده شد',
    cmt: 'پیام',
    edt: 'درخواست ویرایش شد',
    acc: 'درخواست پذیرفته شد',
    pay: 'وام پرداخت شد',
    rej: 'درخواست رد شد',
    cnl: 'درخواست را لغو کردید',
    lik: 'یک مدیر موافقت کرد',
}

const MANAGER_EVENT_LABELS: Record<ILoanDemandEventKind, string> = {
    ...EVENT_LABELS,
    cnl: 'عضو درخواست را لغو کرد',
}

const STATE_LABELS: Record<ILoanDemandState, string> = {
    dmnd: 'در انتظار بررسی',
    insp: 'در حال بررسی',
    accp: 'پذیرفته شد',
    rjcd: 'رد شد',
    cnld: 'لغو شد',
}

const STATE_TONE: Record<ILoanDemandState, string> = {
    dmnd: 'bg-warning-soft text-warning-on',
    insp: 'bg-warning-soft text-warning-on',
    accp: 'bg-success-2 text-success-on',
    rjcd: 'bg-danger-soft text-danger-on',
    cnld: 'bg-canvas text-text-secondary',
}

/**
 * Whether this visit came from the SMS or from inside the app. Same reasoning
 * as the withdrawal-request detail page: a tap in the app is a router PUSH
 * against a document we rendered, while the SMS link is a fresh load through
 * the backend's `/l/<id>/` redirect and arrives as POP with a foreign or
 * absent referrer.
 */
const arrivalSource = (navigationType: string) => {
    if (navigationType !== 'POP') return 'app'
    if (!document.referrer) return 'sms'
    try {
        return new URL(document.referrer).origin === window.location.origin
            ? 'app'
            : 'sms'
    } catch {
        return 'sms'
    }
}

/**
 * One درخواست وام and everything that has happened to it — the کارتابل.
 *
 * This page exists because of the silence. A member files the single most
 * consequential ask they ever make of their fund, sees «در انتظار بررسی», and
 * then hears nothing until a decision lands. From the outside a fund still
 * discussing the request and a fund that never opened it are identical. The
 * managers' discussion, meanwhile, happened in a Telegram group, so the reason
 * for the answer never reached the person it was about.
 *
 * It is the target of the پیگیری link in every SMS this feature sends, reached
 * through the backend's short `/l/<id>/` redirect — and those SMSes go to
 * managers as well as to the requester, so both sides must land on something
 * that makes sense. `viewer` in the payload is what decides which.
 */
export default function LoanDemandDetail() {
    const params = useParams()
    const navigate = useNavigate()
    const navigationType = useNavigationType()
    const queryClient = useQueryClient()
    const [searchParams] = useSearchParams()
    const [draft, setDraft] = useState('')

    const demandId = params?.id
    const fundId = searchParams.get('fund')

    const { data, isLoading, isError, error, refetch } = useQuery(
        ['loan-demand', fundId, demandId],
        getLoanDemand,
        { enabled: Boolean(fundId && demandId) }
    )

    // 403 means «not your request and you don't manage this fund» — permanent.
    // Offering «تلاش دوباره» for it sends people round a loop that can never
    // end differently.
    const status = (error as { response?: { status?: number } })?.response
        ?.status
    const notAllowed = status === 403 || status === 404
    // The API route carries the fund in the path, so without `?fund=` the query
    // stays disabled — which also means `isLoading` is FALSE. Rendering off
    // `isLoading || data` alone would show a blank page, which is exactly what
    // a link missing the parameter would do.
    const missingFund = !fundId

    const isManager = Boolean(data?.viewer?.is_manager)
    const isRequester = Boolean(data?.viewer?.is_requester)
    const eventLabels = isManager ? MANAGER_EVENT_LABELS : EVENT_LABELS

    const comment = useMutation(postLoanDemandComment, {
        onSuccess: () => {
            posthog.capture('loan_demand_comment_posted', {
                fund_id: fundId,
                demand_id: demandId,
                author: isRequester ? 'requester' : 'manager',
                // never the text itself
                length: draft.trim().length,
            })
            setDraft('')
            queryClient.invalidateQueries(['loan-demand', fundId, demandId])
        },
        onError: () => {
            toast.error('پیام ثبت نشد. دوباره تلاش کنید.')
        },
    })

    const like = useMutation(toggleLoanDemandLike, {
        onSuccess: () => {
            queryClient.invalidateQueries(['loan-demand', fundId, demandId])
        },
        onError: () => {
            toast.error('ثبت نشد. دوباره تلاش کنید.')
        },
    })

    const priority = data ? demandPriority(data.demand_priority) : null

    return (
        <div className=" min-h-screen bg-canvas pb-40">
            <Header title="درخواست وام" />

            <div className=" px-4 pt-4">
                {missingFund && (
                    <SectionError message="این لینک ناقص است — شمارهٔ صندوق را ندارد. از داخل برنامه وارد شوید." />
                )}

                {!missingFund && isLoading && <PulseCard />}

                {!missingFund && isError && notAllowed && (
                    <SectionError message="این درخواست وام مال شما نیست و مدیر این صندوق هم نیستید." />
                )}

                {!missingFund && isError && !notAllowed && (
                    <SectionError
                        message="درخواست وام باز نشد."
                        onRetry={refetch}
                    />
                )}

                {data && (
                    <>
                        {/* ── the request itself ───────────────────────── */}
                        <div className=" rounded-2xl border border-stroke bg-white p-4">
                            <div className=" flex items-start justify-between gap-2">
                                <div className=" min-w-0">
                                    <p className=" text-lg font-bold text-heading">
                                        {toman(data.amount)}
                                    </p>
                                    <p className=" mt-1 text-[11px] text-text-secondary">
                                        {faPlain.format(data.num_repayments)}{' '}
                                        قسط
                                        {' · '}
                                        {isManager
                                            ? data.member_name
                                            : data.moneypool_name}
                                    </p>
                                </div>
                                <span
                                    className={` shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${
                                        STATE_TONE[data.demand_state]
                                    }`}
                                >
                                    {STATE_LABELS[data.demand_state]}
                                </span>
                            </div>

                            {priority && (
                                <p className=" mt-2 text-[11px] text-text-secondary">
                                    {priority.label} — {priority.need}
                                </p>
                            )}

                            {/* A member who never opened the app should not
                                find a request under their name with no
                                explanation of where it came from. */}
                            {data.registered_by_manager && (
                                <p className=" mt-3 rounded-lg bg-canvas px-3 py-2 text-[11px] leading-6 text-text-body">
                                    {isRequester
                                        ? `این درخواست را ${data.registrar_name} (مدیر صندوق) از طرف شما ثبت کرده است.`
                                        : `این درخواست را ${data.registrar_name} از طرف عضو ثبت کرده است.`}
                                </p>
                            )}

                            {data.user_description && (
                                <p className=" mt-3 border-t border-divider pt-3 text-[11px] leading-6 text-text-body">
                                    {isRequester ? 'توضیح شما' : 'توضیح عضو'}:{' '}
                                    {data.user_description}
                                </p>
                            )}
                        </div>

                        {/* ── endorsements ─────────────────────────────── */}
                        <div className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
                            <div className=" flex items-center justify-between gap-2">
                                <p className=" text-sm font-bold text-heading">
                                    موافقت مدیران
                                </p>
                                <p className=" text-[11px] text-text-secondary">
                                    {faPlain.format(data.likes.count)} از{' '}
                                    {faPlain.format(data.managers.length)}
                                </p>
                            </div>

                            {/* The ask was explicitly «کی لایک کرده و کی نه»,
                                so every manager is listed — the ones who have
                                not answered are the point, and they cannot be
                                derived from a list of the ones who have. */}
                            <ul className=" mt-3 flex flex-wrap gap-2">
                                {data.managers.map((manager) => (
                                    <li
                                        key={manager.poolship_id}
                                        className={` rounded-full px-3 py-1 text-[11px] ${
                                            manager.liked
                                                ? ' bg-success-2 font-bold text-success-on'
                                                : ' bg-canvas text-text-secondary'
                                        }`}
                                    >
                                        {manager.liked ? '✓ ' : ''}
                                        {manager.name}
                                    </li>
                                ))}
                            </ul>

                            {data.managers.length === 0 && (
                                <p className=" mt-2 text-[11px] text-text-muted">
                                    این صندوق مدیر دیگری ندارد.
                                </p>
                            )}

                            {isManager && (
                                <button
                                    type="button"
                                    disabled={like.isLoading}
                                    onClick={() => {
                                        posthog.capture(
                                            'loan_demand_like_toggled',
                                            {
                                                fund_id: fundId,
                                                demand_id: demandId,
                                                liked: !data.likes.you_liked,
                                            }
                                        )
                                        like.mutate({
                                            moneypoolId: fundId!,
                                            demandId: demandId!,
                                        })
                                    }}
                                    className={` mt-4 w-full rounded-full py-3 text-sm font-bold ${
                                        data.likes.you_liked
                                            ? ' border border-green-1 bg-white text-green-1'
                                            : ' bg-green-1 text-white'
                                    }`}
                                >
                                    {data.likes.you_liked
                                        ? 'موافقت شما ثبت شده — برداشتن'
                                        : 'با این درخواست موافقم'}
                                </button>
                            )}
                        </div>

                        {/* ── the timeline ─────────────────────────────── */}
                        <p className=" mb-2 mt-5 px-1 text-sm font-bold text-heading">
                            چه اتفاقی افتاده
                        </p>
                        {/* The backend derives «درخواست وام ثبت شد» from the
                            request itself, so this list is never legitimately
                            empty. It was, for every request created before the
                            کارتابل shipped — an empty rounded box under the
                            heading, reported on demand 9347. Keeping a real
                            empty state means a future gap says so instead of
                            rendering a blank card. */}
                        {(data.events ?? []).length === 0 ? (
                            <p className=" rounded-2xl border border-stroke bg-white px-4 py-4 text-center text-[11px] leading-6 text-text-muted">
                                هنوز رویدادی برای این درخواست ثبت نشده است.
                            </p>
                        ) : (
                        <ol className=" rounded-2xl border border-stroke bg-white px-4 py-2">
                            {(data.events ?? []).map((event) => {
                                const isComment = event.kind === 'cmt'
                                return (
                                    <li
                                        key={event.id}
                                        className=" flex gap-3 border-b border-divider py-3 last:border-b-0"
                                    >
                                        <span
                                            aria-hidden
                                            className={` mt-1 h-2 w-2 shrink-0 rounded-full ${
                                                event.kind === 'acc' ||
                                                event.kind === 'pay'
                                                    ? ' bg-success-1'
                                                    : event.kind === 'rej'
                                                    ? ' bg-danger-on'
                                                    : isComment
                                                    ? ' bg-brand'
                                                    : ' bg-stroke'
                                            }`}
                                        />
                                        <span className=" min-w-0 flex-1">
                                            <span className=" block text-xs font-bold text-text-body">
                                                {isComment
                                                    ? event.actor_is_requester
                                                        ? isRequester
                                                            ? 'پیام شما'
                                                            : `پیام ${event.actor_name}`
                                                        : `پیام ${
                                                              event.actor_name ||
                                                              'مدیر'
                                                          }`
                                                    : eventLabels[event.kind]}
                                            </span>
                                            <span className=" mt-[2px] block text-[11px] text-text-muted">
                                                {longJalali(event.created)}
                                                {!isComment && event.actor_name
                                                    ? ` · ${event.actor_name}`
                                                    : ''}
                                            </span>
                                            {event.note && (
                                                <span
                                                    className={` mt-1 block whitespace-pre-line text-[11px] leading-6 ${
                                                        isComment
                                                            ? ' rounded-lg bg-canvas px-3 py-2 text-text-body'
                                                            : ' text-text-secondary'
                                                    }`}
                                                >
                                                    {event.note}
                                                </span>
                                            )}
                                        </span>
                                    </li>
                                )
                            })}
                        </ol>
                        )}

                        {/* ── the composer ─────────────────────────────── */}
                        {data.viewer.can_comment && (
                            <div className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
                                <label
                                    htmlFor="loan-demand-comment"
                                    className=" block text-sm font-bold text-heading"
                                >
                                    پیام بگذارید
                                </label>
                                <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                                    {isRequester
                                        ? 'مدیران صندوق پیام شما را می‌بینند و برایشان پیامک می‌رود.'
                                        : 'عضو پیام شما را می‌بیند و برایش پیامک می‌رود.'}
                                </p>
                                <textarea
                                    id="loan-demand-comment"
                                    value={draft}
                                    onChange={(event) =>
                                        setDraft(event.target.value)
                                    }
                                    maxLength={1000}
                                    rows={3}
                                    placeholder={
                                        isManager
                                            ? 'مثلاً: ضامن دومت کیه؟'
                                            : 'مثلاً: آقای احمدی ضامن می‌شود.'
                                    }
                                    className=" mt-3 w-full resize-none rounded-lg border border-stroke bg-canvas p-3 text-xs leading-6 outline-none focus:border-brand"
                                />
                                <button
                                    type="button"
                                    disabled={
                                        comment.isLoading || !draft.trim()
                                    }
                                    onClick={() =>
                                        comment.mutate({
                                            moneypoolId: fundId!,
                                            demandId: demandId!,
                                            note: draft.trim(),
                                        })
                                    }
                                    className=" mt-3 w-full rounded-full bg-green-1 py-3 text-sm font-bold text-white disabled:opacity-50"
                                >
                                    {comment.isLoading
                                        ? 'در حال ارسال…'
                                        : 'ارسال پیام'}
                                </button>
                            </div>
                        )}

                        {/* ── what a manager can do about it ───────────── */}
                        {isManager && data.is_open && (
                            <button
                                type="button"
                                onClick={() => {
                                    posthog.capture(
                                        'loan_grant_started_from_demand',
                                        {
                                            fund_id: fundId,
                                            demand_id: demandId,
                                        }
                                    )
                                    navigate(
                                        `/loans/management/grant/${fundId}`
                                    )
                                }}
                                className=" mt-5 w-full rounded-full bg-green-1 py-3 text-sm font-bold text-white"
                            >
                                بررسی و اعطای وام
                            </button>
                        )}

                        {isManager && (
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(`/loans/requested-loans/${fundId}`)
                                }
                                className=" mt-3 w-full rounded-full border border-green-1 bg-white py-3 text-sm font-bold text-green-1"
                            >
                                همهٔ درخواست‌های وام
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
